"use client"

import type { User } from "better-auth"
import type { Member } from "better-auth/plugins/organization"
import { MenuDots as EllipsisIcon, UserId as UserCogIcon, UserCross as UserXIcon } from "@papier/ui/components/icons"
import { useContext, useState } from "react"

import { AuthUIContext } from "../../lib/auth-ui-provider"
import { cn } from "@papier/ui/lib/utils"
import type { AuthLocalization } from "../../localization/auth-localization"
import type { SettingsCardClassNames } from "../settings/shared/settings-card"
import { Button } from "@papier/ui/components/ui/button"
import { Card } from "@papier/ui/components/ui/card"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from "@papier/ui/components/ui/dropdown-menu"
import { UserView } from "../user-view"
import { LeaveOrganizationDialog } from "./leave-organization-dialog"
import { RemoveMemberDialog } from "./remove-member-dialog"
import { UpdateMemberRoleDialog } from "./update-member-role-dialog"

export interface MemberCellProps {
    className?: string
    classNames?: SettingsCardClassNames
    member: Member & { user?: Partial<User> | null }
    localization?: AuthLocalization
    hideActions?: boolean
}

export function MemberCell({
    className,
    classNames,
    member,
    localization: localizationProp,
    hideActions,
    onLeaveOrganizationSuccess
}: MemberCellProps & { onLeaveOrganizationSuccess?: () => Promise<void> }) {
    const {
        organization: organizationOptions,
        hooks: {
            useListMembers,
            useSession,
            useListOrganizations,
            useHasPermission
        },
        localization: contextLocalization
    } = useContext(AuthUIContext)
    const localization = { ...contextLocalization, ...localizationProp }

    const { data: sessionData } = useSession()
    const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
    const [leaveDialogOpen, setLeaveDialogOpen] = useState(false)
    const [updateRoleDialogOpen, setUpdateRoleDialogOpen] = useState(false)

    const builtInRoles = [
        { role: "owner", label: localization.OWNER },
        { role: "admin", label: localization.ADMIN },
        { role: "member", label: localization.MEMBER }
    ]

    const { data } = useListMembers({
        query: { organizationId: member.organizationId }
    })

    const members = data?.members

    const myRole = members?.find(
        (m) => m.user?.id === sessionData?.user.id
    )?.role
    const isOnlyOneOwner =
        members?.filter((m) => m.role === "owner").length === 1
    const roles = [...builtInRoles, ...(organizationOptions?.customRoles || [])]
    const role = roles.find((r) => r.role === member.role)

    const isSelf = sessionData?.user.id === member?.userId

    const { data: organizations } = useListOrganizations()
    const organization = organizations?.find(
        (org) => org.id === member.organizationId
    )

    const { data: hasPermissionToUpdateMember } = useHasPermission({
        organizationId: member.organizationId,
        permissions: { member: ["update"] }
    })

    const { data: hasPermissionToRemoveMember } = useHasPermission({
        organizationId: member.organizationId,
        permissions: { member: ["delete"] }
    })
    const canUpdate = hasPermissionToUpdateMember?.success &&
        (member.role !== "owner" || (myRole === "owner" && !isOnlyOneOwner))
    const canRemove = member.role !== "owner" || (myRole === "owner" && !isOnlyOneOwner)
    const canLeave = isSelf && (member.role !== "owner" || !isOnlyOneOwner)

    return (
        <>
            <Card
                className={cn(
                    "grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-2 p-4 sm:flex sm:flex-row",
                    className,
                    classNames?.cell
                )}
            >
                <UserView
                    user={member.user}
                    localization={localization}
                    className="min-w-0 flex-1"
                    classNames={{ content: "min-w-0", title: "whitespace-normal break-words", subtitle: "whitespace-normal break-all" }}
                />

                <span className="col-start-1 pl-10 text-xs opacity-70 sm:pl-0">{role?.label}</span>

                {!hideActions &&
                    (canUpdate || canLeave || (!isSelf && canRemove && hasPermissionToRemoveMember?.success)) && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    className={cn(
                                        "relative col-start-2 row-start-1 row-span-2 ms-auto min-h-11 min-w-11",
                                        classNames?.button,
                                        classNames?.outlineButton
                                    )}
                                    size="icon"
                                    type="button"
                                    variant="outline"
                                    aria-label={`Actions for ${member.user?.name || member.user?.email || localization.MEMBER}`}
                                >
                                    <EllipsisIcon
                                        className={classNames?.icon}
                                    />
                                </Button>
                            </DropdownMenuTrigger>

                            <DropdownMenuContent
                                align="start"
                            >
                                {canUpdate && (
                                        <DropdownMenuItem
                                            onClick={() =>
                                                setUpdateRoleDialogOpen(true)
                                            }
                                        >
                                            <UserCogIcon
                                                className={classNames?.icon}
                                            />
                                            {localization?.UPDATE_ROLE}
                                        </DropdownMenuItem>
                                    )}

                                {(canLeave || (!isSelf && canRemove && hasPermissionToRemoveMember?.success)) && <DropdownMenuItem
                                    onClick={() =>
                                        isSelf
                                            ? setLeaveDialogOpen(true)
                                            : setRemoveDialogOpen(true)
                                    }
                                    variant="destructive"
                                >
                                    <UserXIcon className={classNames?.icon} />
                                    {isSelf
                                        ? localization?.LEAVE_ORGANIZATION
                                        : localization?.REMOVE_MEMBER}
                                </DropdownMenuItem>}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
            </Card>

            <RemoveMemberDialog
                open={removeDialogOpen}
                onOpenChange={setRemoveDialogOpen}
                member={member}
                classNames={classNames}
                localization={localization}
            />

            {organization && (
                <LeaveOrganizationDialog
                    open={leaveDialogOpen}
                    onOpenChange={setLeaveDialogOpen}
                    organization={organization}
                    classNames={classNames}
                    localization={localization}
                    onLeaveOrganizationSuccess={onLeaveOrganizationSuccess}
                />
            )}

            <UpdateMemberRoleDialog
                open={updateRoleDialogOpen}
                onOpenChange={setUpdateRoleDialogOpen}
                member={member}
                classNames={classNames}
                localization={localization}
            />
        </>
    )
}
